type Level = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace'
type LevelWithSilent = Level | 'silent'

type SerializerFn = (value: any) => any

export type LoggerOptions = {
  [key: string]: any
  level?: string
  serializers?: {
    [key: string]: SerializerFn;
  }
}

export interface LogFunction {
  (msg: string, ...args: any[]): void
  (obj: object, msg?: string, ...args: any[]): void
}

export interface BaseLogger {
    child(bindings: LoggerOptions): Logger
    fatal: LogFunction
    error: LogFunction
    warn: LogFunction
    info: LogFunction
    debug: LogFunction
    trace: LogFunction
    flush(): void
}

export type Logger = BaseLogger

export const SimpleConsoleLogger: Logger = {
  child(_bindings: LoggerOptions): Logger {
    return this
  },
  trace(message: any, ...args: any[]) {
    console.trace(message, ...args)
  },
  debug(message: any, ...args: any[]) {
    console.debug(message, ...args)
  },
  info(message: any, ...args: any[]) {
    console.log(message, ...args)
  },
  warn(message: any, ...args: any[]) {
    console.warn(message, ...args)
  },
  error(message: any, ...args: any[]) {
    console.error(message, ...args)
  },
  fatal(message: any, ...args: any[]) {
    console.error(message, ...args)
  },
  flush() {}
}

let defaultLogLevel: string = 'warn'

export function setDefaultLogLevel(level: string) {
  defaultLogLevel = level
}

function NoLogOp() {
  return () => {}
}

export function VoidLogger(): Logger {
  return {
    fatal: NoLogOp(),
    error: NoLogOp(),
    warn: NoLogOp(),
    info: NoLogOp(),
    debug: NoLogOp(),
    trace: NoLogOp(),
    child: () => VoidLogger(),
    flush: () => {}
  } as Logger
}

export function buildLogger(scope: string): Logger
export function buildLogger(baseLogger: Logger, options: LoggerOptions): Logger
export function buildLogger(baseLogger: Logger, scope: string, options: LoggerOptions): Logger
export function buildLogger(_param1: string|Logger, _param2?: string|LoggerOptions, param3?: LoggerOptions): Logger {
  const baseLogger = typeof _param1 === 'string' ? VoidLogger() : _param1 ?? VoidLogger()
  const scope = typeof _param1 === 'string' ? _param1 : typeof _param2 === 'string' ? _param2 : undefined
  const options = typeof _param2 === 'object' ? _param2 : typeof param3 === 'object' ? param3 : {}

  options.scope = scope

  return baseLogger.child(options)
}

// --- InMemoryLog ---

const FORMAT_REGULAR_EXPRESSION = /(%?)(%([jds]))/g

export interface LogEntry {
  level: number
  time: number
  defaults: object
  params: any[]
}

export interface LogLine {
  level: number
  time: number
  msg?: string
  [key: string]: any
}

export const LogLevels = {
  fatal: 60,
  error: 50,
  warn: 40,
  info: 30,
  debug: 20,
  trace: 10
}

export interface InMemoryLogOptions {
  size?: number
  defaults?: object
  sanitize?: (value: any) => any
}

const IN_MEMORY_DEFAULT_LOG_OPTIONS: InMemoryLogOptions = {
  size: 1000,
  defaults: null,
  sanitize: undefined,
}

export class InMemoryLog {
  private _store: LogEntry[]
  private _globalDefaults = []
  private _index = -1
  private _options: InMemoryLogOptions

  constructor(options: InMemoryLogOptions = {}) {
    this._options = { ...IN_MEMORY_DEFAULT_LOG_OPTIONS, ...options }

    if (options.size) {
      this._store = new Array(options.size)
      this._index = this._store.length
    } else {
      this._store = []
    }
  }

  public child(defaultObject: object = null): Logger {
    const sanitize = this._options.sanitize
    const log: any = createLogFunction(this, defaultObject, LogLevels.info, sanitize)
    log.trace = createLogFunction(this, defaultObject, LogLevels.trace, sanitize)
    log.debug = createLogFunction(this, defaultObject, LogLevels.debug, sanitize)
    log.info = createLogFunction(this, defaultObject, LogLevels.info, sanitize)
    log.warn = createLogFunction(this, defaultObject, LogLevels.warn, sanitize)
    log.error = createLogFunction(this, defaultObject, LogLevels.error, sanitize)
    log.fatal = createLogFunction(this, defaultObject, LogLevels.fatal, sanitize)

    log.child = (childDefaultObject: object) => {
      const combinedDefault = { ...defaultObject, ...childDefaultObject }
      return this.child(combinedDefault)
    }

    log.flush = () => {}

    return <Logger> log
  }

  public write(entry: LogEntry) {
    if (this._index === -1) {
      this._store.unshift(entry)
    } else {
      if (this._index === 0) {
        this._index = this._store.length
      }

      this._index -= 1
      this._store[this._index] = entry
    }
  }

  public *enumerate(count = 100): IterableIterator<LogLine> {
    return yield* Array.from(this.enumerateBackwards(count)).reverse()
  }

  public *enumerateBackwards(count = 100): IterableIterator<LogLine> {
    const startIndex = this._index === -1 ? 0 : this._index
    let circled = false
    let returnedCount = 0

    for (let index = startIndex ;; index += 1) {
      if (index === this._store.length) {
        index = 0
        circled = true
      }

      if (circled && index === startIndex) {
        break
      }

      const entry = this._store[index]

      if (!entry) {
        return
      }

      const entryParams = entry.params
      let item
      let formattedMessage

      if (typeof entryParams[0] === 'string') {
        item = null
        const message = entryParams[0]
        const messageParameters = entryParams.slice(1)
        formattedMessage = format(message, ...messageParameters)
      } else if (typeof entryParams[1] === 'string') {
        item = entryParams[0]
        const message = entryParams[1]
        const messageParameters = entryParams.slice(2)
        formattedMessage = format(message, ...messageParameters)
      } else if (entryParams[1]) {
        console.error('If first parameter is not a string, second parameter can only be a string', entryParams)
        continue
      } else {
        item = entryParams[0]
      }

      const logLine: LogLine = { ...entry.defaults, ...item, ...this._options.defaults, msg: formattedMessage || '', level: entry.level, time: entry.time }

      yield logLine
      returnedCount++

      if (returnedCount >= count) {
        return <LogLine> { ...entry.defaults, ...this._options.defaults, msg: 'LOG LIMIT REACHED', level: LogLevels.debug, time: Date.now() }
      }
    }
  }
}

function createLogFunction(log: InMemoryLog, defaults: object, level: number = LogLevels.info, sanitize?: (value: any) => any): LogFunction {
  return function logFunction(...params) {
    const cleanedParams = sanitize ? params.map(p => sanitize(p)) : params
    log.write({ defaults, level, time: Date.now(), params: cleanedParams })
  }
}

function format(formatString: string, ...args: any[]) {
  if (args.length) {
    formatString = formatString.replace(FORMAT_REGULAR_EXPRESSION, (match, escaped, ptn, flag) => {
      let arg = args.shift()

      switch (flag) {
        case 's':
          arg = '' + arg
          break
        case 'd':
          arg = Number(arg)
          break
        case 'j':
          arg = JSON.stringify(arg)
          break
      }

      if (!escaped) {
        return arg
      }

      args.unshift(arg)

      return match
    })
  }

  if (args.length) {
    formatString += ' <<' + args.length + ' unmatched arg(s): ' + args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(', ') + '>>'
  }

  formatString = formatString.replace(/%{2,2}/g, '%')

  return '' + formatString
}
